<template>
  <v-container fluid>
    <v-card class="mx-16 mt-6" max-height="calc(100vh - 100px)">
      <v-responsive class="overflow-y-auto">
        <v-data-table
          class="full-screen-table elevation-1 row-pointer"
          :headers="headers"
          :items="groups"
          :page.sync="page"
          :items-per-page="itemsPerPage"
          hide-default-footer
          disable-sort
          fixed-header
          :loading="loadingGroups"
          :loading-text="getLabel('loading')"
          :no-data-text="getLabel('table_no_data')"
          @click:row="selectRow"
        >
        <template #item="{ item }">
            <tr @click="selectRow(item, $event)" @click.middle.prevent="goToGroupProfile(item, $event)">
              <td v-for="header in headers" :key="header.value">     
                <template>
                  <v-row justify="center">
                    {{ item[header.value] }}
                  </v-row>
                </template>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-responsive>
      <v-container>
        <v-row justify="center">
          <v-col cols="6">
            <v-container class="max-width">
              <v-pagination v-if="totalPages != -1" v-model="page" :length="totalPages" />
            </v-container>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </v-container>
</template>

<script>
import Vue from 'vue';
import { onMounted, ref, computed } from '@vue/composition-api';
import { useRouter, useRoute } from 'vue2-helpers/vue-router';
import { useQuery, useMutation } from '@vue/apollo-composable';

import { getGraphqlSchema } from '../../graphql/index';
import { useMainStore } from '@/store/index';
import { getLabel, EventBus } from '@/utils/index';

export default {
  name: 'GroupsDashboard',
  setup () {
    const router = useRouter();

    const route = useRoute();

    const store = useMainStore();

    if (route.name !== 'groupsSearch') {
      store.cleanSearch();
    }

    const headers = [
      // {
      //   text: getLabel('active'),
      //   value: 'online',
      //   align: 'left'
      // },
      {
        text: getLabel('name'),
        value: 'name',
        align: 'center'
      },
      {
        text: getLabel('collaborators'),
        value: 'collaborators_count',
        align: 'center'
      }
    ];

    const isToAddGroupMode = () => store.addGroupMode;

    const itemsPerPage = 30;

    const page = computed({
      get () {
        return store.search.page;
      },
      // setter
      set (newValue) {
        store.search.page = newValue;
      }
    });

    const totalGroups = ref(0);

    const sortbyName = [{ 'name.raw': 'asc' }];

    const variables = computed(() => (
      {
        query: {
          bool: {
            must: [
              {
                simple_query_string: {
                  query: `${store.search.query ? store.search.query : ''}*`,
                  fields: ['name', 'uuid']
                }
              }
            ],
            filter: [{
              terms: {
                wtype: [
                  'collaborator_group'
                ]
              }
            }]
          }
        },
        from: (store.search.page * itemsPerPage) - (itemsPerPage),
        size: itemsPerPage,
        sort: !store.search.query
          ? sortbyName
          : []
      }));

    const { result, loading: loadingGroups, onResult } = useQuery(getGraphqlSchema('searchCollaborators'), variables, () => ({ fetchPolicy: 'no-cache' }));

    onResult(r => {
      if (r.loading) {
        return;
      }

      totalGroups.value = r.data.searchResults.count;
    });

    const groups = computed(() => (loadingGroups.value ? [] : result.value?.searchResults.items));

    const totalPages = computed(() => (totalGroups.value > 0 ? Math.ceil(totalGroups.value / itemsPerPage) : -1));

    // mutation that adds collaborator to group
    let groupToAdd = null;

    const { mutate: mutationAddCollaboratorToGroup, onDone: onDoneAdd, onError: onErrorAdd } = useMutation(getGraphqlSchema('addCollaboratorToGroup'), () => ({
      variables: {
        uuid: groupToAdd.uuid,
        collaborator: store.collaborator.uuid
      }
    }));

    onErrorAdd(error => {
      store.loading = false;

      if (error.message === 'COLLABORATOR_ALREADY_IN_GROUP') {
        EventBus.$emit('show-notifications', [{
          message: getLabel('collaborator_in_group_error'),
          type: 'danger'
        }]);
      } else {
        EventBus.$emit('show-notifications', [{
          message: getLabel('collaborator_add_group_error'),
          type: 'danger'
        }]);
      }

      router.push({ name: 'manageProfile', params: { uuid: store.collaborator.uuid } });
    });

    onDoneAdd(r => {
      store.loading = false;

      if (r.data.collaborator_add_collaborator_to_group.success) {
        store.collaborator.groups.push(r.data.collaborator_add_collaborator_to_group.group);
        EventBus.$emit('show-notifications', [{
          message: getLabel('collaborator_added_group'),
          type: 'success'
        }]);
      } else {
        EventBus.$emit('show-notifications', [{
          message: getLabel('collaborator_add_group_error'),
          type: 'danger'
        }]);
      }

      router.push({ name: 'manageProfile', params: { uuid: store.collaborator.uuid } });
    });
    /** ********************************************* */

    const selectRow = (g,event) => {
      if (isToAddGroupMode()) {
        groupToAdd = g;

        store.loading = true;
        mutationAddCollaboratorToGroup();
      } else {
        goToGroupProfile(g, event);
      }
    };

    const goToGroupProfile = (g, event) => {
      const routerResolve = router.resolve({ name: 'manageGroup', params: { uuid: g.uuid } });
      if (event.button === 1) {
        window.open(routerResolve.href, '_blank');
        return;
      }

      router.push(routerResolve.normalizedTo);
    }

    const updateGroupList = updatedGroup => {
      const indexToChange = result.value.searchResults.items.findIndex(g => g.uuid === updatedGroup.uuid);

      Vue.set(result.value.searchResults.items, indexToChange, { ...result.value.searchResults.items[indexToChange], ...updatedGroup });
    };

    const deleteGroupFromList = deletedGroupUuid => {
      const indexToDelete = result.value.searchResults.items.findIndex(g => g.uuid === deletedGroupUuid);
      result.value.searchResults.items.splice(indexToDelete, 1);
    };

    onMounted(() => {
      EventBus.$on('group-delete', deleteGroupFromList);

      EventBus.$on('update-group', updateGroupList);
    });

    return {
      getLabel,
      headers,
      isToAddGroupMode,
      itemsPerPage,
      page,
      loadingGroups,
      groups,
      totalPages,
      selectRow,
      goToGroupProfile
    };
  }

};

</script>
